/** Prefill the agent composer with a Mermaid diagram (and optional render error). */

export type MermaidChatDocumentReference = {
  id: string
  title?: string
  path?: string
  type?: string
}

export type OpenChatPromptDetail = {
  query: string
  newThread?: boolean
  /** When set, composer inserts a doc chip (same as copy-from-editor paste). */
  documentReference?: MermaidChatDocumentReference
}

export function buildMermaidChatPrompt(options: {
  source: string
  error?: string | null
  /** When a doc chip is attached, omit redundant UUID from the prompt body. */
  attachDocumentChip?: boolean
}): string {
  const source = options.source.trim()
  const fence = ['```mermaid', source, '```'].join('\n')
  if (options.error) {
    const docHint = options.attachDocumentChip
      ? 'The attached document chip is the target file.'
      : 'Use the currently open document from context.'
    return [
      'This Mermaid diagram in the current document failed to render.',
      '',
      `Error: ${options.error}`,
      '',
      'Source:',
      fence,
      '',
      `${docHint} Fix the syntax and **apply the change directly** with **patchDocument** (old_string/new_string on the exact broken line in the fence). Do **not** use full-body updateDocument on large docs.`,
      'Common footguns: quote labels that contain `/` or `{}` (e.g. `API["/api/{kind}/"]`).',
    ].join('\n')
  }
  return [
    'Regarding this Mermaid diagram in the current document:',
    '',
    fence,
    '',
    'Please review it and suggest improvements if needed.',
  ].join('\n')
}

/** Open/expand chat and set the composer draft (same event as command palette). */
export function openMermaidInChat(options: {
  source: string
  error?: string | null
  documentId?: string | null
  documentTitle?: string | null
  documentPath?: string | null
}): void {
  if (typeof window === 'undefined') return
  const attachDocumentChip = Boolean(options.documentId?.trim())
  const documentReference = attachDocumentChip
    ? {
        id: options.documentId!.trim(),
        title:
          options.documentTitle?.trim() ||
          options.documentPath?.split('/').pop() ||
          'Document',
        path: options.documentPath?.trim() || options.documentId!.trim(),
        type: 'note',
      }
    : undefined

  window.dispatchEvent(
    new CustomEvent('flownotes:open-chat-prompt', {
      detail: {
        query: buildMermaidChatPrompt({
          source: options.source,
          error: options.error,
          attachDocumentChip,
        }),
        newThread: false,
        documentReference,
      } satisfies OpenChatPromptDetail,
    }),
  )
}
